import { inject, Injectable, NgZone } from "@angular/core";
import { collection, doc, Firestore, onSnapshot, orderBy, query, where } from "@angular/fire/firestore";
import { Data } from "@angular/router";
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  defer,
  filter,
  map,
  Observable,
  of,
  ReplaySubject,
  shareReplay,
  startWith,
  switchMap,
  take,
  withLatestFrom
} from "rxjs";
import { AuthStateService } from "@core/auth-state.service";
import { CommonService } from "./common/common.service";
import { FirestoreDataService } from "./firestore-data.service";
import { NotificationsService } from "./notifications.service";
import { UserManagementService } from "./user-management.service";

@Injectable({
  providedIn: "root",
})
export class HomeDataService {
  userSpecialFeatures: Data;
  homePageLoaded$ = new BehaviorSubject<boolean>(false);
  private homePageInitialized = false;
  private commonService = inject(CommonService);
  private authStateService = inject(AuthStateService);
  private ngZone = inject(NgZone);
  constructor(
    private firestore: Firestore,
    private firestoreDataService: FirestoreDataService,
    private notificationService: NotificationsService,
    private userManagementService: UserManagementService,

  ) { }

  public initializeHomePage(): void {
    if (!this.homePageInitialized) {
      this.homePageInitialized = true;
      this.homePageLoaded$.next(true);
    }
  }
  getAddHomePopupStatus() {
    return this.commonService.getUid("home-data-service").pipe(
      switchMap((userId) => {
        if (!userId) {
          console.error("UserId not found in getAddHomePopupStatus");
          return of(null);
        }

        return this.firestoreDataService
          .getAngularFireStore(`users/${userId}/add-to-hs/list`, "get")
          .pipe(
            map((res: Data) => res?.data()),
            shareReplay(1)
          );
      })
    );
  }

  hideHomeScreenPopup(Node: string) {
    this.commonService.getUid("hideHomeScreenPopup").pipe(take(1)).subscribe({
      next: (userId) => {
        if (!userId) {
          console.error("UserId not found in hideHomeScreenPopup");
          return;
        }

        const path_id = "77QWvo3LfPewFyiow0TD";
        const doc_data = { [Node]: true };
        const method = "update";

        this.firestoreDataService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          method,
        }).subscribe({
          error: (err) => {
            this.notificationService
              .getConstantsMsg("1015")
              .then((translatedMsg) => {
                const defaultMsg = {
                  message: {
                    line_1: "Something went wrong!",
                    line_2: "",
                  },
                  style: {
                    icon: "tick circle error.svg",
                    type: "Error",
                  },
                };
                this.notificationService.showToastMessage(translatedMsg, defaultMsg);
              })
              .catch((notificationErr) => {
                console.error("Error fetching notification constants:", notificationErr); // Log notification error
                return notificationErr;
              });
            console.error('Error setting home screen popup flag:', err);
            return err;
          }
        });
      }
    })
  }


  feedHomeCards(userId: string) {
    this.firestoreDataService.getV2CloudFunction("gamificationsetupv2", userId)?.subscribe();
  }

  // this method calls the CF to trigger new member checklist card related events
  newMemberChecklistCard(userId: string) {
    this.firestoreDataService.getV2CloudFunction("assignnewmemberchecklistv2", userId)?.subscribe();
  }

  // this function is used to load the home date when the user wants to
  // view the home date. It is used in the home.component.ts file
  loadHomeDate$ = new ReplaySubject(1);

  // This code is used to load a user's home screen
  // The user's home screen is loaded when the user is logged in
  // The user's home screen is loaded only once, when the user is logged in
  newHomeCards$ = this.loadHomeDate$.pipe(
    filter((f: boolean) => f),
    withLatestFrom(this.userManagementService.userRequest$),
    switchMap(([homePageLoaded, user]: [boolean, Data]) => {
      return user && user?.uid
        ? this.firestoreDataService
          .getAngularFireStore(`/users/${user?.uid}/in_app_notification/status`, "snapshotChanges")
          .pipe(
            map((data: Data) => data?.payload?.data()),
            shareReplay(1)
          )
        : of(null);
    }),
    shareReplay(1)
  );

  // This code is used to get the default home card. The default home card is used to determine which card is displayed on the home page.
  defaultHomeCard$: Observable<Data> = defer(() =>
    this.firestoreDataService
      .getAngularFireStore("/Global_lookup/general_cards", "snapshotChanges")
      .pipe(
        map((data: Data) => {
          try {
            const value = data?.payload?.data();
            if (!value || typeof value !== 'object') {
              return {};
            } else {
              const safeValue = { ...value };
              Object.keys(safeValue).forEach((key) => {
                if (safeValue[key]?.card_type === "new_member_checklist") {
                  delete safeValue[key];
                  return;
                }
                // Normalize content fields: Firestore may store arrays as numeric-keyed map
                // objects (e.g. {0: {...}, 1: {...}}). Convert to a real array so *ngFor
                // can iterate. Only convert when ALL keys are numeric indexes to avoid
                // silently corrupting legitimate object-shaped content fields.
                const content = safeValue[key]?.content;
                if (content && !Array.isArray(content) && typeof content === "object") {
                  const contentKeys = Object.keys(content);
                  // Only convert when keys form a zero-based contiguous sequence
                  // ("0", "1", "2", ..., n-1), which is the exact shape Firestore produces
                  // when it stores a JS array as a map. This avoids corrupting objects
                  // whose keys happen to be numeric IDs, years, or sparse indexes.
                  const sorted = [...contentKeys].sort((a, b) => Number(a) - Number(b));
                  const isZeroBasedContiguous =
                    sorted.length > 0 &&
                    sorted.every((k, i) => k === String(i));
                  if (isZeroBasedContiguous) {
                    safeValue[key] = {
                      ...safeValue[key],
                      content: sorted.map((k) => content[k]),
                    };
                  }
                }
              });
              return safeValue;
            }
          } catch (error) {
            console.error("Error processing /Global_lookup/general_cards data:", error);
            return {};
          }
          }),
        catchError((error) => {
          console.error("Snapshot error in defaultHomeCard$:", error);
          return of({});
        }),
        shareReplay(1)
      )
  );


  // 1. Get the user
  // 2. Wait for the custom token to be ready (prevents permission-denied race condition on iOS)
  // 3. Open the homeCardsVM$ Firestore listener
  // 4. If homeCardsVM$ is empty, call the API to load homeCardsVM$ from the server
  homeCardsVM$ = this.userManagementService.userRequestForHome$?.pipe(
    switchMap((user: Data) => {
      if (!user?.uid) {
        return of({});
      }
      // Gate on customTokenReady$ so the onSnapshot never opens before auth is confirmed.
      // ReplaySubject(1) means returning users get this immediately with zero extra delay.
      return this.authStateService.customTokenReady$.pipe(
        take(1),
        switchMap(() => {
          const docRef = doc(this.firestore, `users/${user.uid}/user_HomeCard/list`);

          return new Observable((observer) => {
            const unsubscribe = onSnapshot(
              docRef,
              (docSnap) => {
                const homeData = docSnap.exists() ? docSnap.data() : null;
                if (!homeData || Object.keys(homeData).length < 1) {
                  this.loadHomeData(user.uid).subscribe();
                }
                // Push into Angular zone so OnPush consumers refresh without a page reload.
                this.ngZone.run(() => observer.next(homeData || {}));
              },
              (error) => {
                // Log permission errors specifically
                const errorLog = this.errorHandlerLogs("Error fetching homeCardsVM$ - Firebase permission or network error", error);
                console.error("Firebase Error Details:", errorLog);
                // Return safe default empty object instead of null to prevent cascading errors
                this.ngZone.run(() => observer.next({}));
              }
            );
            return () => unsubscribe();
          }).pipe(
            catchError((err) => {
              this.errorHandlerLogs('Error in homeCardsVM$ observable', err);
              // Return empty object as fallback instead of null
              return of({});
            }),
            shareReplay(1)
          );
        })
      );
    }),
    startWith({}),
    shareReplay(1)
  );

  prizeDrawFeature$ = defer(() =>
    // Gate on customTokenReady$ before opening the special_features listener
    this.authStateService.customTokenReady$.pipe(
      take(1),
      switchMap(() =>
        this.commonService.getUid("prizeDrawFeature").pipe(
          switchMap((userId) => {
            if (!userId) {
              return of(null); // fallback when no user logged in
            }
            return this.firestoreDataService
              .getAngularFireStore(
                `/users/${userId}/special_features/features`,
                "snapshotChanges"
              )
              .pipe(
                map((data: Data) => {
                  const featureData = data?.payload?.data();
                  return featureData || null;
                }),
                catchError((error) => {
                  console.error('Error fetching prize draw features - Firebase permission or network error:', error);
                  return of(null);
                }),
                shareReplay(1)
              );
          })
        )
      )
    )
  );

  // This code combines the latest values from the observables
  // homeCardsVM$, defaultHomeCard$, and prizeDrawFeature$
  // and checks if the user is logged in, if so, then it returns
  // the combined values
  homeCards$ = combineLatest(this.homeCardsVM$, this.defaultHomeCard$, this.prizeDrawFeature$).pipe(
    withLatestFrom(this.userManagementService.userRequestForHome$),
    filter(([[v1, v2, v4], v3]) => v3 && v3?.phoneNumber != "") as any,
    map(([[r1, r2, r4], r3]) => {
      // Safely handle special features
      if (r4 && typeof r4 === 'object') {
        this.userSpecialFeatures = { special_features: r4 };
      }

      // Validate r1 and r2 are objects before spreading
      const safeR1 = (r1 && typeof r1 === 'object') ? r1 : {};
      const safeR2 = (r2 && typeof r2 === 'object') ? r2 : {};
      const safeSpecialFeatures = this.userSpecialFeatures || {};

      // Merge with validation
      const res = { ...safeR1, ...safeR2, ...safeSpecialFeatures };
      return res;
    }),
    catchError((error) => {
      console.error('Error in homeCards$ combineLatest:', error);
      return of({});
    }),
    shareReplay(1)
  );

  /**
   * This function calls the loadHomeData function in the backend to load the user's home data.
   */
  loadHomeData(userId) {
    if (userId) {
      return this.firestoreDataService.getV2CloudFunction("loadhomedatav2", userId);
    }
  }

  // This method hides the onboarding popup
  hideOnboardingPopup(userNode: string) {
    this.commonService.getUid("hideOnboardingPopup").pipe(take(1)).subscribe({
      next: (userId) => {
        if (!userId) return; // Guard clause
        const path_id = `aiUM9R8c5J9Zai5FQiOh`;
        const doc_data = { [userNode]: true };
        const method = "update";
        this.commonService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          method,
        }).subscribe({
          error: (err) => {
            this.notificationService
              .getConstantsMsg("1015")
              .then((translatedMsg) => {
                const defaultMsg = {
                  message: {
                    line_1: "Something went wrong!",
                    line_2: "",
                  },
                  style: {
                    icon: "tick circle error.svg",
                    type: "Error",
                  },
                };
                this.notificationService.showToastMessage(translatedMsg, defaultMsg);
              })
              .catch((notificationError) => {
                return notificationError;
              });
            return err;
          },
        });
      },
      error: (err) => {
        return err;
      },
    });
  }

  // To solve firebase permission issues we are commenting out the vip_status card for now, but keeping the code here for reference when we want to enable it again in the future.
  
  // This code is used to get the user's vip_status details.
  // Gated on customTokenReady$ to prevent permission-denied on iOS Safari
  // when the listener opens before the Firebase custom token is available.
  // inline_notification$ = this.authStateService.customTokenReady$.pipe(
  //   take(1),
  //   switchMap(() =>
  //     this.userManagementService.userRequest$?.pipe(
  //       switchMap((user: Data) => {
  //         return user && user?.uid
  //           ? this.firestoreDataService
  //             .getAngularFireStore(`users/${user?.uid}/inline_notification/vip_status`, "snapshotChanges")
  //             .pipe(
  //               map((res: Data) => {
  //                 return res?.payload?.data();
  //               }),
  //               shareReplay(1)
  //             )
  //           : of({});
  //       })
  //     )
  //   )
  // );

  // This code is used to get the inbox messages from firestore.
  // Gated on customTokenReady$ to prevent permission-denied race condition on iOS.
  userInbox$: Observable<Data[] | null> = this.homePageLoaded$?.pipe(
    filter((homePageLoaded: boolean) => homePageLoaded),
    withLatestFrom(this.userManagementService.userRequest$),
    switchMap(([homePageLoaded, user]: [boolean, Data]) => {
      if (!user || !user.uid) {
        return of([]); // Return an empty array if no user or UID
      }
      // Wait for the custom token before opening the Firestore query listener
      return this.authStateService.customTokenReady$.pipe(
        take(1),
        switchMap(() => {
          const inboxCollection = collection(this.firestore, `/users/${user.uid}/inbox`);
          const inboxQuery = query(
            inboxCollection,
            where("hide", "==", false),
            orderBy("creation_date", "desc")
          );

          return new Observable<Data[] | null>((observer) => {
            const unsubscribe = onSnapshot(
              inboxQuery,
              (snapshot) => {
                const inboxMessages: Data[] = snapshot.docs.map((doc) => ({
                  id: doc.id,
                  ...doc.data(),
                }));

                // Sort by expiry_date and viewed status
                inboxMessages.sort((a, b) => {
                  const dateA = a.expiry_date?.toDate ? a.expiry_date.toDate() : null;
                  const dateB = b.expiry_date?.toDate ? b.expiry_date.toDate() : null;

                  if (dateA && dateB) {
                    return dateA.getTime() - dateB.getTime();
                  }
                  if (!dateA && !dateB) {
                    return 0;
                  }
                  if (!dateA) {
                    return 1;
                  }
                  return -1;
                });
                inboxMessages.sort((a, b) => a.viewed - b.viewed);

                // Filter out expired messages based on expiry_date
                const now = new Date();
                const filteredMessages = inboxMessages.filter((message) => {
                  const expiryDate = message.expiry_date?.toDate ? message.expiry_date.toDate() : null;
                  return !expiryDate || expiryDate >= now;
                });

                observer.next(filteredMessages.length ? filteredMessages : null);
              },
              (error) => {
                if ((error as { code?: string })?.code === "permission-denied") {
                  observer.next(null);
                  observer.complete();
                  return;
                }
                observer.error(error);
              }
            );

            // Clean up on unsubscribe
            return () => unsubscribe();
          });
        })
      );
    }),
    shareReplay(1) // Cache results
  );

  // This method is used to log error messages
  errorHandlerLogs(message: string, error?: Data): { message: string; timestamp: string; error: Data } {
    const log = {
      message,
      timestamp: new Date().toISOString(),
      error: error ? this.formatError(error) : null,
    };
    return log;
  }

  // This method is used to format error messages
  private formatError(error: Data): Data {
    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };
    }
    return error;
  }
}
